/**
 * TA10 Fittings and Contents Form — client-facing fittings grid.
 *
 * Schema-driven: the canonical schema (sections → fittings rows, each tied to
 * its official AcroForm field) is served by /api/ta10/schema. The grid writes
 * exactly the keys the TA10 AcroForm mapper reads back, so the client's
 * answers populate the genuine Law Society PDF on submit:
 *
 *   {key}_status   ∈ "included" | "excluded" | "none"
 *   {key}_price    free text (separate-purchase price)
 *   {key}_comment  free text
 *   {key}_fitting  ∈ "fitted" | "freestanding"   (kitchen rows only)
 *
 * TA10 is delivered by field-filling + flattening the official PDF (not
 * reportlab), so key alignment with the schema is load-bearing.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, ChevronLeft, ChevronRight, AlertTriangle,
  Loader2, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import "../styles/client-forms.css";
// White-label firm name — resolved from REACT_APP_FIRM_NAME (see
// config/constants.js). Never hardcode the firm string in source.
import { FIRM_NAME } from "../config/constants";

const API = process.env.REACT_APP_BACKEND_URL || "";


function KoiMark() {
  return (
    <div className="bonsai-koi-mark">
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label={FIRM_NAME}>
        <ellipse cx="13" cy="20" rx="9" ry="5.5" transform="rotate(-30 13 20)" fill="rgba(67, 56, 202,0.9)" />
        <ellipse cx="13" cy="20" rx="5" ry="3" transform="rotate(-30 13 20)" fill="rgba(79, 70, 229,0.6)" />
        <circle cx="8" cy="15" r="1.2" fill="white" opacity="0.9" />
        <path d="M22 4 Q26 8 24 13" stroke="rgba(67, 56, 202,0.5)" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        <ellipse cx="27" cy="20" rx="9" ry="5.5" transform="rotate(30 27 20)" fill="rgba(180,90,10,0.9)" />
        <ellipse cx="27" cy="20" rx="5" ry="3" transform="rotate(30 27 20)" fill="rgba(210,120,20,0.6)" />
        <circle cx="32" cy="25" r="1.2" fill="white" opacity="0.9" />
        <path d="M18 36 Q14 32 16 27" stroke="rgba(180,90,10,0.5)" strokeWidth="1.2" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: "included", label: "Included", color: "bg-teal/30 border-teal text-teal" },
  { value: "excluded", label: "Excluded", color: "bg-status-red/20 border-red-500/50 text-status-red" },
  { value: "none", label: "None", color: "bg-slate-700/50 border-slate-600 text-slate-300" },
];

const FITTING_OPTIONS = [
  { value: "fitted", label: "Fitted" },
  { value: "freestanding", label: "Freestanding" },
];

function SectionHeader({ number, title }) {
  return (
    <div className="mb-4 mt-6 first:mt-0">
      <div className="flex items-center gap-3">
        <span className="w-7 h-7 rounded-full bg-teal/20 flex items-center justify-center text-teal text-xs font-bold shrink-0">{number}</span>
        <h3 className="text-base font-bold text-white">{title}</h3>
      </div>
    </div>
  );
}

function FittingRow({ row, d, set }) {
  const key = row.key;
  const status = d[`${key}_status`] || "";
  const price = d[`${key}_price`] || "";
  const comment = d[`${key}_comment`] || "";
  const fitting = d[`${key}_fitting`] || "";
  // Not every row offers a "None at property" state (see schema has_none).
  const options = row.has_none === false
    ? STATUS_OPTIONS.filter(o => o.value !== "none")
    : STATUS_OPTIONS;

  return (
    <div className="bg-navy border border-white/5 rounded-lg px-4 py-3 space-y-2" data-testid={`item-${key}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-sm text-slate-300">{row.label}</span>
        <div className="flex gap-1.5">
          {options.map(opt => (
            <button key={opt.value} type="button"
              onClick={() => set(`${key}_status`, opt.value)}
              className={`px-2.5 py-1 rounded text-xs font-medium border transition-all ${
                status === opt.value ? opt.color : "border-white/10 text-slate-500 hover:border-white/20"
              }`}
              data-testid={`${key}-${opt.value}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {row.kitchen && status === "included" && (
        <div className="flex items-center gap-2 ml-4" data-testid={`${key}-fitting`}>
          <span className="text-[10px] text-slate-500">Fitted or freestanding?</span>
          <div className="flex gap-1.5">
            {FITTING_OPTIONS.map(opt => (
              <button key={opt.value} type="button"
                onClick={() => set(`${key}_fitting`, opt.value)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-all ${
                  fitting === opt.value
                    ? "bg-teal/30 border-teal text-teal"
                    : "border-white/10 text-slate-500 hover:border-white/20"
                }`}
                data-testid={`${key}-fitting-${opt.value}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {status === "excluded" && (
        <div className="flex items-center gap-2 ml-4">
          <span className="text-[10px] text-slate-500">Available for separate purchase?</span>
          <input value={price} onChange={e => set(`${key}_price`, e.target.value)}
            placeholder="Price (optional)"
            className="w-32 bg-navy border border-white/10 rounded px-2 py-1 text-xs text-white"
            data-testid={`${key}-price`} />
        </div>
      )}

      {status && (
        <input value={comment} onChange={e => set(`${key}_comment`, e.target.value)}
          placeholder="Comments (optional)"
          className="w-full bg-navy border border-white/10 rounded px-2 py-1 text-xs text-white placeholder:text-slate-600"
          data-testid={`${key}-comment`} />
      )}
    </div>
  );
}

function DeclarationSection({ d, set }) {
  return (
    <>
      <SectionHeader number="✓" title="Declaration" />
      <div className="bg-navy border border-teal/30 rounded-lg p-5 space-y-4">
        <p className="text-sm text-slate-300 leading-relaxed">
          I/We confirm that to the best of my/our knowledge, the information given above is correct.
          Items marked as "Included" will be left at the property and are included in the sale price
          unless a separate price has been agreed.
        </p>
        <p className="text-sm text-slate-300 leading-relaxed">
          Items marked as "Excluded" will be removed before completion unless the buyer has agreed to
          purchase them separately at the stated price. This form will become part of the contract when
          contracts are exchanged.
        </p>
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="field-declaration_confirmed">
          <input type="checkbox" checked={!!d.declaration_confirmed}
            onChange={e => set("declaration_confirmed", e.target.checked)}
            className="mt-0.5 rounded border-white/20 bg-navy text-teal focus:ring-teal/30"
            data-testid="input-declaration_confirmed" />
          <span className="text-sm text-slate-300">
            I/We confirm the above declaration and that all answers are truthful and complete
            <span className="text-status-red"> *</span>
          </span>
        </label>
        <div data-testid="field-declaration_signed_by">
          <label className="text-sm text-slate-200 font-medium mb-1.5 block">Signed by (full name) *</label>
          <input value={d.declaration_signed_by || ""}
            onChange={e => set("declaration_signed_by", e.target.value)}
            placeholder="Type your full name as your electronic signature"
            className="w-full bg-navy border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-teal/50 focus:outline-none"
            data-testid="input-declaration_signed_by" />
        </div>
        <div data-testid="field-declaration_date">
          <label className="text-sm text-slate-200 font-medium mb-1.5 block">Date</label>
          <input type="date" value={d.declaration_date || ""}
            onChange={e => set("declaration_date", e.target.value)}
            className="w-full bg-navy border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-teal/50 focus:outline-none"
            data-testid="input-declaration_date" />
        </div>
      </div>
    </>
  );
}

export default function TA10IntakeForm() {
  const { token } = useParams();
  const [schema, setSchema] = useState(null);
  const [ta10, setTa10] = useState(null);
  const [formData, setFormData] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [s, m] = await Promise.all([
          // BM-H604: token-authorised schema (GET /schema is staff-sealed).
          axios.get(`${API}/api/ta10/${token}/schema`),
          axios.get(`${API}/api/ta10/${token}`),
        ]);
        setSchema(s.data);
        setTa10(m.data);
        if (m.data.form_data) setFormData(m.data.form_data);
        if (m.data.current_step) setStep(m.data.current_step);
        if (m.data.status === "submitted") setSubmitted(true);
      } catch (err) {
        console.error("Failed to load TA10:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setField = useCallback((id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  }, []);

  // Steps = each schema section, plus a trailing declaration step.
  const steps = useMemo(() => {
    const sections = (schema?.sections || []).map(sec => ({ kind: "section", section: sec }));
    return [...sections, { kind: "declaration" }];
  }, [schema]);

  const autoSave = useCallback(async (newStep) => {
    if (!token || submitted) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/ta10/${token}/save`,
        { form_data: formData, current_step: newStep ?? step });
    } catch (err) {
      console.error("Auto-save failed:", err);
    } finally {
      setSaving(false);
    }
  }, [token, formData, step, submitted]);

  const validateStep = useCallback((stepIdx) => {
    const errors = [];
    if (steps[stepIdx]?.kind === "declaration") {
      if (!formData.declaration_confirmed) errors.push("You must accept the declaration");
      if (!formData.declaration_signed_by?.trim()) errors.push("Signature (full name) is required");
    }
    return errors;
  }, [steps, formData]);

  const handleNext = useCallback(() => {
    const errors = validateStep(step);
    if (errors.length) { setValidationErrors(errors); return; }
    setValidationErrors([]);
    const newStep = Math.min(step + 1, steps.length - 1);
    setStep(newStep);
    autoSave(newStep);
  }, [step, steps, validateStep, autoSave]);

  const handleBack = useCallback(() => {
    setValidationErrors([]);
    const newStep = Math.max(step - 1, 0);
    setStep(newStep);
    autoSave(newStep);
  }, [step, autoSave]);

  const handleSubmit = async () => {
    const errors = validateStep(step);
    if (errors.length) { setValidationErrors(errors); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/ta10/${token}/submit`, { form_data: formData });
      setSubmitted(true);
    } catch (err) {
      console.error("Submit failed:", err);
      setValidationErrors(["Submission failed. Please try again."]);
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = useCallback(() => {
    window.open(`${API}/api/ta10/${token}/pdf`, "_blank");
  }, [token]);

  if (loading) return (
    <div className="bonsai-client-root" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Loader2 className="w-8 h-8 text-teal animate-spin" />
    </div>
  );

  if (!ta10 || !schema) return (
    <div className="bonsai-client-root" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="text-center p-8 bg-navy-card rounded-xl border border-red-500/30 max-w-md" style={{ position: "relative", zIndex: 1 }}>
        <AlertTriangle className="w-10 h-10 text-status-red mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white mb-2">Form Not Found</h2>
        <p className="text-sm text-slate-400">This link may have expired or is invalid. Please contact {FIRM_NAME}.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="bonsai-client-root" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="text-center p-8 bg-navy-card rounded-xl border border-teal/30 max-w-md" style={{ position: "relative", zIndex: 1 }} data-testid="ta10-submitted">
        <CheckCircle className="w-10 h-10 text-teal mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white mb-2">Thank You</h2>
        <p className="text-sm text-slate-400 mb-4">Your fittings and contents form has been submitted to {FIRM_NAME}.</p>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="ta10-download-pdf">
          <Download className="w-4 h-4 mr-1" /> Download PDF
        </Button>
      </div>
    </div>
  );

  const current = steps[step];
  const isLastStep = step === steps.length - 1;

  return (
    <div className="bonsai-client-root">
      <header className="bonsai-client-header">
        <hr className="bonsai-koi-divider" />
        <div className="bonsai-client-header-inner">
          <KoiMark />
          <div className="bonsai-header-text">
            <h1>{FIRM_NAME}</h1>
            <p>{schema.title || "TA10 Fittings and Contents Form"}</p>
          </div>
        </div>
        <hr className="bonsai-koi-divider" />
      </header>
      <div className="bonsai-client-content">
        <div className="bg-navy-card border border-white/10 rounded-xl p-5 mb-6" data-testid="ta10-intro">
          <p className="text-sm text-slate-300 leading-relaxed">
            Dear <strong className="text-white">{ta10?.client_name}</strong>, please indicate which fittings
            and contents are included in the sale, excluded, or not present at the property.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Property: <span className="text-slate-300">{ta10?.property_address || "(to be confirmed)"}</span>
            {ta10?.matter_reference && <> &middot; Matter: <span className="text-slate-300">{ta10.matter_reference}</span></>}
          </p>
          <div className="flex gap-4 mt-3 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal"></span> Included in sale</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-status-red"></span> Excluded from sale</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500"></span> Not present at property</span>
          </div>
        </div>

        <div className="h-1 bg-slate-800 rounded-full mb-6 overflow-hidden">
          <div className="h-1 bg-teal rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="bg-navy-card border border-white/10 rounded-xl p-5 sm:p-7" data-testid="ta10-section-content">
          {current?.kind === "section" && (
            <>
              <SectionHeader number={current.section.number} title={current.section.title} />
              <div className="space-y-2">
                {(current.section.rows || []).map(row => (
                  <FittingRow key={row.key} row={row} d={formData} set={setField} />
                ))}
              </div>
            </>
          )}
          {current?.kind === "declaration" && (
            <DeclarationSection d={formData} set={setField} />
          )}
        </div>

        {validationErrors.length > 0 && (
          <div className="bg-status-red-dim/20 border border-red-500/30 rounded-lg p-3 mt-4" data-testid="ta10-validation-errors">
            {validationErrors.map((e, i) => (
              <p key={i} className="text-xs text-status-red flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3 shrink-0" /> {e}
              </p>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-6" data-testid="ta10-nav-buttons">
          <Button variant="outline" onClick={handleBack} disabled={step === 0}
            className="border-white/10 text-slate-400 disabled:opacity-30" data-testid="ta10-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              Step {step + 1} of {steps.length}
              {saving && <span className="text-teal ml-2">Saving...</span>}
            </span>
            {isLastStep ? (
              <Button onClick={handleSubmit} disabled={saving} className="bg-teal text-white hover:bg-teal" data-testid="ta10-submit">
                {saving ? "Submitting..." : "Submit Form"}
              </Button>
            ) : (
              <Button onClick={handleNext} className="bg-teal text-white hover:bg-teal" data-testid="ta10-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
